import styles from "./SeriesLegend.module.css";
import { useSectionLayout } from "../layout/sectionLayoutContext";

/** One entry of a {@link SeriesLegend}: a series' toggle key, its label, and its (already-resolved) color. */
export interface SeriesLegendEntry {
  key: string;
  label: string;
  color: string;
  /** `"square"` for bars/pies, `"line"` for line series. Defaults to `"square"`. */
  shape?: "square" | "line";
}

/**
 * The space a {@link SeriesLegend} takes in a grid layout, left empty. A chart that has no legend renders this
 * beneath itself, so its panel is as tall as its neighbours' panels, which carry a legend.
 */
export function LegendReservation(): React.JSX.Element | null {
  const { mode } = useSectionLayout();
  if (mode === "single") {
    return null;
  }

  return <div className={`${styles.legend} ${styles.legendTwoRows}`} aria-hidden="true" />;
}

/**
 * A chart legend rendered explicitly from a caller-supplied, ordered list of entries, rather than
 * Recharts' auto-generated `<Legend>`.
 *
 * Recharts does not reliably keep a stacked bar chart's auto-generated legend in the same order
 * its series were declared in (it can reorder entries relative to the visual stack), which
 * previously caused a chart's legend labels/colors to drift out of sync with both the shared
 * palette order (pink, blue, yellow, ...) and other UI - like vendor cards or tables - built from
 * the same underlying, stably-ordered list. Every multi-series chart should build its `entries`
 * from that same stably-ordered list (in the same order used for `getSeriesColor`) and render
 * this component instead of `<Legend>`, so the legend is always authoritative rather than
 * Recharts-derived.
 *
 * @param props.entries - The series to show, in the exact order they should be displayed.
 * @param props.isHidden - Whether a given entry's key is currently toggled off.
 * @param props.onToggle - Called with an entry's key when its legend item is clicked.
 */
export function SeriesLegend({
  entries,
  isHidden,
  onToggle,
}: {
  entries: SeriesLegendEntry[];
  isHidden: (key: string) => boolean;
  onToggle: (key: string) => void;
}): React.JSX.Element {
  const { mode } = useSectionLayout();

  return (
    <ul className={mode !== "single" ?`${styles.legend} ${styles.legendTwoRows}` : styles.legend}>
      {entries.map((entry) => (
        <li key={entry.key}>
          <button
            type="button"
            className={styles.legendItem}
            aria-pressed={!isHidden(entry.key)}
            onClick={() => onToggle(entry.key)}
          >
            <span
              className={`${styles.swatch} ${entry.shape === "line" ? styles.swatchLine : styles.swatchSquare}`}
              style={{ background: entry.color }}
            />
            {entry.label}
          </button>
        </li>
      ))}
    </ul>
  );
}
